import { useCallback, useRef, useMemo } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ParentSize } from '@visx/responsive';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AreaClosed } from '@visx/shape';
import { AxisBottom } from '@visx/axis';
import { Group } from '@visx/group';
import { Brush } from '@visx/brush';
import BaseBrush from '@visx/brush/lib/BaseBrush';
import { curveMonotoneX } from '@visx/curve';
import type { Bounds } from '@visx/brush/lib/types';
import type { TimeRange } from '../../api/types';

export interface TimeSeriesPoint {
  time: number;
  value: number;
}

interface TimelineBrushProps {
  sparklineData: TimeSeriesPoint[];
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  onPresetChange: (label: string) => void;
  presets: { label: string; value: TimeRange }[];
  activePreset: string | null;
  loading?: boolean;
}

const CHART_HEIGHT = 60;
const MARGIN = { top: 4, right: 16, bottom: 24, left: 16 };

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  presets: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  presetBtn: {
    minWidth: 40,
    padding: '2px 10px',
    fontSize: 11,
    textTransform: 'none',
  },
  rangeLabel: {
    textAlign: 'center',
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

function formatRangeLabel(from: string, to: string): string {
  const fromMs = Number(from);
  const toMs = Number(to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return '';
  const fmt = (ms: number) => {
    const d = new Date(ms);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };
  const diffMin = Math.round((toMs - fromMs) / 60000);
  const label = diffMin >= 60 ? `${Math.round(diffMin / 60)}h` : `${diffMin}m`;
  return `${fmt(fromMs)} — ${fmt(toMs)} (${label})`;
}

export const TimelineBrush = ({
  sparklineData,
  timeRange,
  onTimeRangeChange,
  onPresetChange,
  presets,
  activePreset,
  loading,
}: TimelineBrushProps) => {
  const classes = useStyles();
  const brushRef = useRef<BaseBrush | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const rangeLabel = useMemo(
    () => formatRangeLabel(timeRange.from, timeRange.to),
    [timeRange],
  );

  const onBrushChange = useCallback(
    (domain: Bounds | null) => {
      if (!domain) return;
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const from = String(Math.round(domain.x0));
        const to = String(Math.round(domain.x1));
        onTimeRangeChange({ from, to });
      }, 300);
    },
    [onTimeRangeChange],
  );

  const handlePreset = useCallback(
    (preset: { label: string; value: TimeRange }) => {
      onPresetChange(preset.label);
      // Reset brush visual
      brushRef.current?.reset();
    },
    [onPresetChange],
  );

  if (!sparklineData.length && !loading) return null;

  return (
    <Box className={classes.root}>
      <ParentSize>
        {({ width }) => {
          if (width < 100) return null;
          const innerW = width - MARGIN.left - MARGIN.right;
          const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

          const data = sparklineData.length
            ? sparklineData
            : [
                { time: Date.now() - 7 * 86400000, value: 0 },
                { time: Date.now(), value: 0 },
              ];

          const xScale = scaleTime({
            domain: [
              new Date(data[0].time),
              new Date(data[data.length - 1].time),
            ],
            range: [0, innerW],
          });

          const yScale = scaleLinear({
            domain: [0, Math.max(...data.map(d => d.value)) * 1.2 || 1],
            range: [innerH, 0],
            nice: true,
          });

          return (
            <svg width={width} height={CHART_HEIGHT}>
              <Group left={MARGIN.left} top={MARGIN.top}>
                <AreaClosed
                  data={data}
                  x={d => xScale(new Date(d.time))}
                  y={d => yScale(d.value)}
                  yScale={yScale}
                  curve={curveMonotoneX}
                  fill="#1976d2"
                  fillOpacity={0.15}
                  stroke="#1976d2"
                  strokeWidth={1}
                />
                <AxisBottom
                  top={innerH}
                  scale={xScale}
                  numTicks={Math.min(6, Math.floor(innerW / 100))}
                  tickLabelProps={() => ({
                    fontSize: 10,
                    textAnchor: 'middle' as const,
                    fill: '#999',
                  })}
                  strokeWidth={0}
                  tickStroke="#ccc"
                />
                <Brush
                  xScale={xScale}
                  yScale={yScale}
                  width={innerW}
                  height={innerH}
                  innerRef={brushRef}
                  resizeTriggerAreas={['left', 'right']}
                  brushDirection="horizontal"
                  onChange={onBrushChange}
                  selectedBoxStyle={{
                    fill: '#1976d2',
                    fillOpacity: 0.15,
                    stroke: '#1976d2',
                    strokeWidth: 1,
                  }}
                  useWindowMoveEvents
                />
              </Group>
            </svg>
          );
        }}
      </ParentSize>

      {/* Quick preset buttons */}
      <Box className={classes.presets}>
        {presets.map(p => (
          <Button
            key={p.label}
            className={classes.presetBtn}
            variant={activePreset === p.label ? 'contained' : 'outlined'}
            color={activePreset === p.label ? 'primary' : 'default'}
            size="small"
            onClick={() => handlePreset(p)}
          >
            {p.label}
          </Button>
        ))}
      </Box>

      {/* Custom range label */}
      {rangeLabel && (
        <Typography className={classes.rangeLabel}>{rangeLabel}</Typography>
      )}
    </Box>
  );
};
